import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthContext';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Shield, Lock, User, Bell, CheckCircle, ArrowRight, Eye, EyeOff
} from 'lucide-react';

const STEPS = [
  { id: 'password', label: 'Set Password',   icon: Lock },
  { id: 'profile',  label: 'Your Profile',   icon: User },
  { id: 'notify',   label: 'Notifications',  icon: Bell },
  { id: 'done',     label: 'All Set',         icon: CheckCircle },
];

export default function AdminOnboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Step 0 — change password
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Step 1 — profile
  const [profile, setProfile] = useState({ name: user?.name || '', phone: '', bio: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Step 2 — notifications (preference only, no API call needed on onboarding)
  const [notifPref, setNotifPref] = useState({ email: true, push: true });
  const [savingNotif, setSavingNotif] = useState(false);

  /* ── Step handlers ── */

  const handlePassword = async (e) => {
    e.preventDefault();
    if (passwords.next !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwords.next.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSavingPw(true);
    try {
      await api.put('/profile/password', {
        currentPassword: passwords.current,
        newPassword: passwords.next,
      });
      // Clear mustChangePassword in local user state
      if (setUser) setUser(prev => ({ ...prev, mustChangePassword: false }));
      toast.success('Password updated');
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update password');
    } finally {
      setSavingPw(false);
    }
  };

  const handleProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put('/profile', { name: profile.name, phone: profile.phone, bio: profile.bio });
      if (setUser) setUser(prev => ({ ...prev, name: profile.name }));
      toast.success('Profile saved');
      setStep(2);
    } catch {
      toast.error('Could not save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleNotif = async () => {
    setSavingNotif(true);
    try {
      await api.put('/profile/notifications', {
        channels: { email: notifPref.email, push: notifPref.push },
      });
      setStep(3);
    } catch {
      // Non-fatal — proceed anyway
      setStep(3);
    } finally {
      setSavingNotif(false);
    }
  };

  const handleDone = () => {
    localStorage.setItem(`admin_onboarding_done_${user?.id}`, '1');
    navigate('/admin');
  };

  /* ── UI ── */

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-12">
      {/* Brand */}
      <div className="flex items-center gap-2 mb-8">
        <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <span className="font-bold text-slate-800 text-lg">SymptoConnect</span>
        <span className="text-xs font-semibold text-slate-400 ml-1">Admin Setup</span>
      </div>

      {/* Step progress */}
      <div className="flex items-center gap-0 mb-8">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <div key={s.id} className="flex items-center">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                done   ? 'bg-emerald-100 text-emerald-700' :
                active ? 'bg-primary text-white shadow-sm' :
                         'bg-slate-100 text-slate-400'
              }`}>
                {done ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-6 h-0.5 mx-1 ${i < step ? 'bg-emerald-300' : 'bg-slate-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-lg border border-slate-100 p-7">

        {/* Step 0 — Change password */}
        {step === 0 && (
          <form onSubmit={handlePassword} className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Set your password</h2>
              <p className="text-sm text-slate-500 mt-1">
                You were assigned a temporary password. Choose a new one to secure your account.
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Temporary password</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    value={passwords.current}
                    onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))}
                    placeholder="From your welcome email"
                    className="w-full px-4 py-2.5 pr-10 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">New password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwords.next}
                  onChange={e => setPasswords(p => ({ ...p, next: e.target.value }))}
                  placeholder="At least 8 characters"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Confirm new password</label>
                <input
                  type="password"
                  required
                  value={passwords.confirm}
                  onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                  placeholder="Repeat new password"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingPw}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {savingPw ? 'Saving…' : 'Set password'}
              {!savingPw && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        )}

        {/* Step 1 — Profile */}
        {step === 1 && (
          <form onSubmit={handleProfile} className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Your profile</h2>
              <p className="text-sm text-slate-500 mt-1">How should other admins identify you?</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Display name</label>
                <input
                  type="text"
                  required
                  value={profile.name}
                  onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Phone <span className="font-normal text-slate-400">(optional)</span></label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 555 000 0000"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Short bio <span className="font-normal text-slate-400">(optional)</span></label>
                <textarea
                  rows={2}
                  value={profile.bio}
                  onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                  placeholder="Your role or department…"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep(2)}
                className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-medium text-sm hover:bg-slate-50 transition-colors">
                Skip
              </button>
              <button type="submit" disabled={savingProfile}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60">
                {savingProfile ? 'Saving…' : 'Save & continue'}
                {!savingProfile && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </form>
        )}

        {/* Step 2 — Notifications */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Notification preferences</h2>
              <p className="text-sm text-slate-500 mt-1">Choose how you want to be alerted about platform events.</p>
            </div>
            <div className="space-y-3">
              {[
                { key: 'email', label: 'Email notifications', desc: 'Security alerts, doctor verifications, system issues' },
                { key: 'push',  label: 'Push notifications',  desc: 'Real-time alerts in the browser' },
              ].map(({ key, label, desc }) => (
                <label key={key} className="flex items-start gap-3 p-4 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={notifPref[key]}
                    onChange={e => setNotifPref(p => ({ ...p, [key]: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 accent-primary"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(3)}
                className="flex-1 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-medium text-sm hover:bg-slate-50 transition-colors">
                Skip
              </button>
              <button onClick={handleNotif} disabled={savingNotif}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60">
                {savingNotif ? 'Saving…' : 'Save & continue'}
                {!savingNotif && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Done */}
        {step === 3 && (
          <div className="text-center space-y-5">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">You're all set!</h2>
              <p className="text-sm text-slate-500 mt-2">
                Your admin account is ready. You have full access to the SymptoConnect platform.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-left">
              {[
                { icon: Shield, label: 'Manage admins',   desc: 'Create and disable admin accounts' },
                { icon: User,   label: 'Verify doctors',  desc: 'Review doctor credentials' },
                { icon: Bell,   label: 'System alerts',   desc: 'Monitor security & compliance' },
                { icon: Lock,   label: 'Audit logs',      desc: 'Track all PHI access' },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="p-3 bg-slate-50 rounded-xl">
                  <Icon className="w-4 h-4 text-primary mb-1.5" />
                  <p className="text-xs font-semibold text-slate-800">{label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
            <button
              onClick={handleDone}
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              Go to Admin Dashboard
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
